import { User, UserManager, WebStorageStateStore } from 'oidc-client-ts';
import { createContext, type ReactNode, useContext, useEffect, useMemo, useState } from 'react';
import type { Config } from './config';

export interface Usuario {
  /** `sub` de Keycloak. */
  id: string;
  nombre: string;
  correo: string;
  tenantId: string | null;
  roles: string[];
  superadmin: boolean;
  /** Roles del realm (superadmin, soporte…; arquitectura 2.12). */
  rolesRealm: string[];
}

interface Sesion {
  usuario: Usuario | null;
  cargando: boolean;
  /** Token vigente para el API (se renueva solo con el refresh token). */
  token: () => Promise<string>;
  entrar: (volver?: string) => Promise<void>;
  salir: () => Promise<void>;
  cambiarClave: () => Promise<void>;
}

const Contexto = createContext<Sesion | null>(null);

/** Claims del access token (JWT); los roles del realm y del cliente solo vienen ahí, no en el ID token. */
function claimsDe(token: string | undefined): Record<string, unknown> {
  const carga = token?.split('.')[1];
  if (!carga) return {};
  try {
    const base64 = carga.replace(/-/g, '+').replace(/_/g, '/');
    const bytes = Uint8Array.from(atob(base64), (c) => c.charCodeAt(0));
    return JSON.parse(new TextDecoder().decode(bytes)) as Record<string, unknown>;
  } catch {
    return {};
  }
}

/** Lee lo que los frentes usan de Keycloak: nombre del ID token; lotero y roles del access token. */
export function usuarioDe(u: User): Usuario {
  const p = u.profile as Record<string, unknown>;
  const a = claimsDe(u.access_token);
  const acceso = (a['resource_access'] ?? p['resource_access']) as
    Record<string, { roles?: string[] }> | undefined;
  const reino = (a['realm_access'] ?? p['realm_access']) as { roles?: string[] } | undefined;
  const tenant = a['tenant_id'] ?? p['tenant_id'];
  const nombre = [p['given_name'], p['family_name']].filter(Boolean).join(' ');
  return {
    id: String(a['sub'] ?? p['sub'] ?? ''),
    nombre: nombre || String(p['name'] ?? p['email'] ?? ''),
    correo: String(p['email'] ?? ''),
    tenantId: typeof tenant === 'string' ? tenant : null,
    roles: acceso?.['plataforma']?.roles ?? [],
    superadmin: reino?.roles?.includes('superadmin') ?? false,
    rolesRealm: reino?.roles ?? [],
  };
}

export function crearGestor(config: Config) {
  const origen = window.location.origin;
  return new UserManager({
    authority: config.emisor,
    client_id: config.clienteId,
    redirect_uri: `${origen}/callback`,
    post_logout_redirect_uri: `${origen}/`,
    response_type: 'code',
    scope: 'openid',
    // El token vive en la pestaña (sessionStorage), no en el disco del equipo.
    userStore: new WebStorageStateStore({ store: window.sessionStorage }),
    automaticSilentRenew: true,
  });
}

export function ProveedorSesion({
  gestor,
  children,
}: {
  gestor: UserManager;
  children: ReactNode;
}) {
  const [usuario, setUsuario] = useState<Usuario | null>(null);
  const [cargando, setCargando] = useState(true);

  useEffect(() => {
    let vivo = true;
    const poner = (u: User | null) => {
      if (vivo) setUsuario(u && !u.expired ? usuarioDe(u) : null);
    };
    const quitar = () => poner(null);
    void gestor.getUser().then((u) => {
      poner(u);
      if (vivo) setCargando(false);
    });
    gestor.events.addUserLoaded(poner);
    gestor.events.addUserUnloaded(quitar);
    gestor.events.addSilentRenewError(quitar);
    return () => {
      vivo = false;
      gestor.events.removeUserLoaded(poner);
      gestor.events.removeUserUnloaded(quitar);
      gestor.events.removeSilentRenewError(quitar);
    };
  }, [gestor]);

  const valor = useMemo<Sesion>(
    () => ({
      usuario,
      cargando,
      token: async () => {
        let u = await gestor.getUser();
        if (u && u.expires_in !== undefined && u.expires_in < 30)
          u = await gestor.signinSilent().catch(() => null);
        if (!u || u.expired) {
          await gestor.signinRedirect({ state: { volver: window.location.pathname } });
          throw new Error('La sesión venció');
        }
        return u.access_token;
      },
      entrar: (volver = '/') => gestor.signinRedirect({ state: { volver } }),
      salir: () => gestor.signoutRedirect(),
      // La contraseña se cambia en la página de Keycloak (kc_action=UPDATE_PASSWORD): nunca pasa
      // por el API de la plataforma (arquitectura 2.12).
      cambiarClave: () =>
        gestor.signinRedirect({
          state: { volver: window.location.pathname },
          extraQueryParams: { kc_action: 'UPDATE_PASSWORD' },
        }),
    }),
    [gestor, usuario, cargando],
  );
  return <Contexto.Provider value={valor}>{children}</Contexto.Provider>;
}

export function useSesion(): Sesion {
  const s = useContext(Contexto);
  if (!s) throw new Error('useSesion fuera de ProveedorSesion');
  return s;
}
